// shameless copy of flagged library. check https://github.com/sergiodxa/flagged
import React from "react";
import Cookies from "universal-cookie";
import PropTypes from "prop-types";
import { useAuth } from "./auth-context";
import { ANALYTICS_V1, defaultOrgFlags, defaultUserFlags } from "../../utils/feaure-flag-constants";

const defaultFeatures = {
    [ANALYTICS_V1]: true,
};

export const FeatureFlagsContext = React.createContext();

const getCookieFFs = () => {
    return new Cookies().get("FF") || {};
};

export const FlagsProvider = ({ features, children }) => {
    const { user } = useAuth();
    const userDefaults =
        user && user.orgId && user.id ? defaultUserFlags[`${user.orgId}:${user.id}`] : {};
    const orgDefaults = user && user.orgId ? defaultOrgFlags[user.orgId] : {};
    const fullFeatures = React.useMemo(
        () => ({
            ...defaultFeatures,
            ...features,
            ...userDefaults,
            ...orgDefaults,
            ...getCookieFFs(),
        }),
        [defaultFeatures, features, getCookieFFs()]
    );
    return (
        <FeatureFlagsContext.Provider value={fullFeatures}>{children}</FeatureFlagsContext.Provider>
    );
};

FlagsProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    features: PropTypes.object,
};

FlagsProvider.defaultProps = {
    features: undefined,
};

// Custom Hook API
export function useFeatures() {
    const features = React.useContext(FeatureFlagsContext);
    if (features === null) {
        throw new Error("You must wrap your components in a FlagsProvider.");
    }
    return features;
}

// Custom Hook API
export function useFeature(name) {
    const features = useFeatures();
    if (!features) return false;
    if (Array.isArray(features)) return features.includes(name);
    if (typeof features[name] === "boolean") return features[name];
    return name.split("/").reduce((featureGroup, featureName) => {
        if (typeof featureGroup === "boolean") return featureGroup;
        if (featureGroup[featureName] === undefined) return false;
        return featureGroup[featureName];
    }, features);
}

// High Order Component API
export function withFeature(featureName) {
    return (Component) => (props) => {
        const hasFeature = useFeature(featureName);
        if (!hasFeature) return null;
        return <Component {...props} />;
    };
}

// Render Prop API
export const Feature = ({ name, children, render = children }) => {
    const hasFeature = useFeature(name);
    if (typeof render === "function") return render(hasFeature);
    if (!hasFeature) return null;
    return render;
};
